import { DataTableSettingsContextProvider } from "@/components/data-table/DataTableSettingsContext";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import Form from "@/components/Form";
import { QueryTable } from "@/components/query-table/QueryTable";
import { useToast } from "@/components/ui/use-toast";
import useQueryData from "@/hooks/useQueryData";
import { ApiError, SuccessFetchListResponse } from "@/services/api";
import {
  ReadTimesheetEntry,
  WriteTimesheetEntry,
} from "@/types/timesheetEntry";
import { ReadTimesheetEntryContractorVisit } from "@/types/timesheetEntryContractorVisit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSyncFormWithQueryData } from "../../../hooks/useSyncFormWithQueryData";
import { writeTimesheetEntryContractorVisitFormSchema } from "../schema";
import { WriteTimesheetEntryContractorVisitForm } from "../types";
import { useTranslation } from "react-i18next";
import { TimesheetEntriesService } from "../services/timesheetEntries";
import SaveButton from "@/components/SaveButton";
import { normalizeWriteTimesheetEntryDatetimes } from "../utils/normalizeWriteTimesheetEntryDatetimes";

export const TimesheetEntriesContractorsVisitsForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const settings = useDataTableSettings<ReadTimesheetEntry>();
  const defaultValues = useMemo(() => {
    return {
      entries: {},
    };
  }, []);

  const { query } =
    useDataTableQuery<
      SuccessFetchListResponse<ReadTimesheetEntryContractorVisit>
    >();
  const data = useQueryData(query);
  const form = useForm<WriteTimesheetEntryContractorVisitForm>({
    resolver: zodResolver(writeTimesheetEntryContractorVisitFormSchema),
    defaultValues,
  });

  const mutation = useMutation<
    ReadTimesheetEntry,
    ApiError,
    WriteTimesheetEntry[]
  >({
    mutationFn: (values: WriteTimesheetEntry[]) =>
      TimesheetEntriesService.patch(values),
    onSuccess: () => {
      query.refetch();
      toast({
        title: t("timesheetEntries.visits.success.update"),
      });
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("timesheetEntries.visits.errors.update"),
        description,
        variant: "destructive",
      });
    },
  });

  const handleSubmit: SubmitHandler<
    WriteTimesheetEntryContractorVisitForm
  > = async (submittedData) => {
    const dirtyFields = form.formState.dirtyFields;
    const payload = Object.keys(dirtyFields.entries || {}).map((key) => {
      return normalizeWriteTimesheetEntryDatetimes(submittedData.entries[key]);
    });
    return mutation.mutateAsync(payload);
  };

  useSyncFormWithQueryData<
    ReadTimesheetEntryContractorVisit,
    WriteTimesheetEntryContractorVisitForm
  >(data, form);

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <DataTableSettingsContextProvider value={settings}>
        <QueryTable.Content>
          <QueryTable.Status />
        </QueryTable.Content>
        <footer className="[grid-area:footer] flex p-2 justify-end gap-6">
          <SaveButton isLoading={query.isFetching} />
        </footer>
      </DataTableSettingsContextProvider>
    </Form>
  );
};
